import { type PdfPageBaseLayout, FontAwesomeIcon, Skeleton, Stack, Text } from "@skailer-com/uikit";
import { useFileObjectUrl } from "@/hooks/use-file-object-url.hook.ts";
import {
  EsignPagesViewer,
  type EsignPagesViewerProps,
} from "@/pages/esign/components/EsignPagesViewer.tsx";
import { useDocumentPreviewState } from "@/pages/employees/employee/widgets/use-document-preview-state.hook.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ApiDocumentPreview } from "@/types/document.types.ts";
import { type ReactNode, useCallback } from "react";
import { useDocumentDetailRefetcher } from "@/queries/use-documents.query.ts";
import { useTranslation } from "react-i18next";

export interface EsignFilePreviewProps {
  file: ApiFile;
  preview: ApiDocumentPreview;
  documentId: number;
  pageNavigationPlacement?: EsignPagesViewerProps["pageNavigationPlacement"];
  pageMarkerRoleIndexes?: Record<number, number[]>;
  renderPageOverlay?: (pageNumber: number, baseLayout: PdfPageBaseLayout) => ReactNode;
  onReachedEnd?: () => void;
  maxRenderWidth?: number;
  controlsRef?: EsignPagesViewerProps["controlsRef"];
}

export function EsignFilePreview({
  file,
  preview,
  documentId,
  pageNavigationPlacement,
  pageMarkerRoleIndexes,
  renderPageOverlay,
  onReachedEnd,
  maxRenderWidth,
  controlsRef,
}: EsignFilePreviewProps) {
  const { t } = useTranslation();

  const refetchDocument = useDocumentDetailRefetcher();
  const refetchPreview = useCallback(
    async () => (await refetchDocument(documentId))?.preview,
    [documentId, refetchDocument]
  );

  const { sourceFile, hasPreviewContract, isPreparing, isReady, isPreviewFailed } =
    useDocumentPreviewState({ file, preview, refetchPreview });

  const canRender = hasPreviewContract ? isReady : true;
  const { url, isLoading, isError } = useFileObjectUrl(canRender ? sourceFile : null);

  if (isPreparing) {
    return (
      <Stack items={"center"} className={"gap-3 py-16"}>
        <FontAwesomeIcon
          icon={"fa-light fa-spinner"}
          className={"animate-spin text-2xl text-secondary"}
        />
        <Text className={"text-title"}>{t("common:document_preview_preparing")}</Text>
        <Text className={"text-secondary"}>
          {t("common:document_preview_preparing_description")}
        </Text>
      </Stack>
    );
  }

  if (isPreviewFailed || isError) {
    return (
      <Stack items={"center"} className={"py-16"}>
        <Text className={"text-danger"}>{t("common:document_preview_failed")}</Text>
      </Stack>
    );
  }

  if (isLoading || !url) {
    return (
      <div className={"mx-auto w-full"} style={{ maxWidth: maxRenderWidth }}>
        <Skeleton className={"h-215 w-full"} />
      </div>
    );
  }

  return (
    <EsignPagesViewer
      src={url}
      pageNavigationPlacement={pageNavigationPlacement}
      pageMarkerRoleIndexes={pageMarkerRoleIndexes}
      renderPageOverlay={renderPageOverlay}
      onReachedEnd={onReachedEnd}
      maxRenderWidth={maxRenderWidth}
      controlsRef={controlsRef}
    />
  );
}
